import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { ApiResponse, ProjectListDto } from "~/api/GeneratedApi";
import { useAxios } from "~/utils/hooks/useAxios";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";

interface UpdateShapeFileProps {
  projectId: number;
  uploadFolderType: UploadFolderType;
  data: FormData;
}
export type UploadFolderType = "progress" | "plan";

interface UploadShapeFileProps {
  projectId: number;
  files: File[];
  projectionId: null | number;
  uploadFolderType: UploadFolderType;
}
export const useProjectShapeUpload = () => {
  const axios = useAxios();
  const qc = useQueryClient();

  const { mutateAsync: updateShapeFile } = useCustomMutation<
    ApiResponse,
    ApiResponse,
    UpdateShapeFileProps
  >(
    ({ projectId, uploadFolderType, data }: UpdateShapeFileProps) =>
      new Promise((resolve, reject) => {
        axios
          .post<ApiResponse>(
            `/api/Project/${projectId}/${uploadFolderType}`,
            data,
          )
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          .then((x) => resolve(JSON.parse(x.data as string)))
          .catch((err) => reject(err));
      }),
  );

  const uploadFilesProgress = useCallback(
    async ({
      files,
      uploadFolderType,
      projectId,
      projectionId,
    }: UploadShapeFileProps) => {
      const data = new FormData();
      files.forEach((f) => {
        data.append("files", f, f.name);
      });
      if (projectionId !== null)
        data.append("coordinateSystemId", String(projectionId));
      const res = await updateShapeFile({ projectId, data, uploadFolderType });
      return res;
    },
    [updateShapeFile],
  );

  const { mutate: mutateShapeFiles, isLoading } = useCustomMutation(
    ({ ...params }: UploadShapeFileProps) => uploadFilesProgress(params),
    {
      onSuccess: (_d, { projectId }) => {
        qc.setQueryData<ProjectListDto | undefined>(
          [projectId, "project"],
          (values) => {
            if (values) {
              return { ...values, processingFiles: true };
            }
            return undefined;
          },
        );
      },

      onError: (_d, { projectId }) => {
        qc.setQueryData<ProjectListDto | undefined>(
          [projectId, "project"],
          (values) => {
            if (values) {
              return { ...values, processingFiles: false };
            }
            return undefined;
          },
        );
      },
    },
  );

  return { mutateShapeFiles, isLoading };
};
